// Pricing FAQ for the dedicated /pricing page.
//
// Every answer here is sourced from copy that already ships in the product
// (the AgencyPricingPlans bullets in agencyTiers.ts + the /producthunt
// highlights). We do not invent policies. If a tier doesn't actually
// deliver a feature today, it doesn't get mentioned here.

import { type ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

type FaqEntry = { value: string; q: string; a: ReactNode };

const FAQS: FaqEntry[] = [
  {
    value: 'free',
    q: 'How does the free trial work?',
    a: 'Click "Start 14-day free trial" on the Free card. No credit card required. Sign in with Google and you get 25 scans to use over 14 days, across every audit tool, fully white-labelled with no Fly logo on your reports or embeds. When the trial ends, pick a plan to keep running tools. Your captured leads and past reports stay readable either way.',
  },
  {
    value: 'scans',
    q: 'How do scans work?',
    a: 'One scan per tool run, whatever the tool. Starter includes 100 scans a month and Pro includes 300, shared across every tool. The one exception is AI Search Visibility, which uses 7 scans because it queries several AI models on each run. Re-running the same business within 24 hours is free and does not use a scan.'
  },
  {
    value: 'currency',
    q: 'What currency will I be billed in?',
    a: 'Prices are shown in USD. Indian agencies are billed in INR at a locked rate of ₹83 to $1, with a GST-compliant tax invoice.',
  },
  {
    value: 'cancel',
    q: 'Can I cancel anytime?',
    a: 'Yes. Both plans can be cancelled anytime, and you keep your scans until the end of the period you have already paid for.',
  },
  {
    value: 'white-label',
    q: 'What does white-label include?',
    a: 'Starter adds your logo plus one brand colour. Pro adds white-label reports and lets you point a custom domain at your tools with your own brand colours.',
  },
  {
    value: 'leads',
    q: 'How are leads delivered to me?',
    a: 'Every plan delivers leads by email and in your dashboard the moment they\'re captured. Pro adds a webhook you can point at your own system, and you can export any lead list to CSV. CRM sync is still on the way.',
  },
];

export function PricingFaq() {
  return (
    <section className="relative bg-gradient-to-b from-gray-950 to-[#0d0d14] py-24 lg:py-28">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.1 }}
          >
            Pricing questions, answered.
          </h2>
        </div>

        <Accordion type="single" collapsible className="max-w-3xl mx-auto space-y-3">
          {FAQS.map(({ value, q, a }) => (
            <AccordionItem
              key={value}
              value={value}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-5 transition-colors hover:border-white/20 data-[state=open]:border-fuchsia-500/30 data-[state=open]:bg-white/[0.05]"
            >
              <AccordionTrigger className="py-5 text-left text-base font-semibold text-white hover:no-underline">
                {q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-white/70">
                {a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
